// libs
import styled, { css } from 'styled-components'
// icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIosRounded'
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit'
import ObjectivesIconOrigin from '@mui/icons-material/TrackChanges'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import MeasuresIconOrigin from 'src/icons/measures.svg'
// themes
import { colors } from 'themes'
// utils
import { ellipsis, size } from 'polished'
// constants
import { CELL_PADDING_X, DEPTH_STEP, FORM_INDENT_OFFSET } from './constants'
// types
import { TDropPosition } from './types'

export const TreeWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${colors.gray290};

  thead {
    position: sticky;
    background: white;
    top: 0;
    z-index: 2;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
  }
`

export const HeadCell = styled.th`
  text-align: left;
  padding: 5px ${CELL_PADDING_X}px 6px ${CELL_PADDING_X}px;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.gray300};
  border-bottom: 1px solid ${colors.gray290};
`

export const RowTr = styled.tr<{
  $clickable?: boolean
  $isDragging?: boolean
  $dimmed?: boolean
  $isRoot: boolean
  $color: string
}>`
  border-bottom: 1px solid ${colors.gray190};
  ${({ $clickable }) => $clickable && 'cursor: pointer;'};
  ${({ $isDragging }) => $isDragging && 'opacity: 0.4;'};
  ${({ $dimmed }) => $dimmed && 'opacity: 0.3;'};

  ${({ $isRoot, $color }) =>
    $isRoot &&
    css`
      ${DataCell} {
        padding: 8px 0;

        // Fills the whole cell so the coloured band stays continuous across columns. The width of
        // the column itself is set by the non-root rows (see CellContent).
        ${CellContent} {
          width: 100%;
          height: 42px;
          display: flex;
          color: white;
          background: ${$color};
          align-items: center;
          padding: 0 ${CELL_PADDING_X}px;
        }
      }

      ${ActionsCell} {
        padding: 8px 0;

        > div {
          height: 42px;
          background: ${$color};
          display: flex;
          align-items: center;
          padding: 0 ${CELL_PADDING_X}px;
        }
      }

      ${DescriptionInnerContent} {
        border-radius: 2px 0 0 2px;
        background: ${$color};

        svg {
          path {
            fill: white !important;
          }
        }

        ${LinkIconWithCounter} {
          color: white;
        }

        ${Title} {
          color: white;
        }

        ${PathText} {
          color: white;
        }
      }
    `}

  &:hover {
    background: ${colors.gray190};
  }
`

// Indented drop indicator: its left offset encodes the projected nesting depth (option C).
export const DropLine = styled.div<{ $depth: number; $position: TDropPosition }>`
  position: absolute;
  left: ${({ $depth }) => ($depth ? 32 + $depth * DEPTH_STEP : 12)}px;
  right: 0;
  height: 2px;
  background: ${colors.blue500};
  pointer-events: none;
  ${({ $position }) => ($position === 'before' ? 'top: -1px;' : 'bottom: -1px;')};

  &::before {
    content: '';
    position: absolute;
    left: -4px;
    top: -3px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.blue500};
  }
`

export const DescriptionCell = styled.td`
  position: relative;
  padding: 8px 0 8px 12px;
  width: 100%;
  max-width: 50vw;
`

// Vertical level-colour strip rendered inline before the drag handle.
export const ColorBar = styled.span<{ $color?: string; $archived?: boolean }>`
  align-self: stretch;
  flex-shrink: 0;
  width: 16px;
  background: ${({ $color }) => $color || 'transparent'};
  border-radius: 2px;

  ${({ $archived }) =>
    $archived &&
    css`
      background: none;
      border: 2px dashed ${colors.gray220};
    `};
`

// Stacks the title and (for referenced items) the path beneath it inside the Name column.
export const TextWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1;
`

export const PathText = styled.span`
  font-size: 12px;
  color: ${colors.gray300};
  ${ellipsis('100%')};
`

// Generic data column, sized to its content width (`width: 1%` in an auto-layout table).
export const DataCell = styled.td`
  padding: 8px ${CELL_PADDING_X}px;
  white-space: nowrap;
  width: 1%;
`

// Content box of a data cell (head and body alike). In an auto-layout table a `width` on the
// `td`/`th` is only a hint the content can override, so the configured width goes on this inner
// block instead: a block of a definite width *is* the column's min/max-content width, which lets a
// column be narrower than its own nowrap content. `max-width` keeps that cap in place for rows that
// override the width (root rows stretch their cell to 100%) — an intrinsic contribution is clamped
// by max-width, so those rows can never widen the column past what the other rows asked for.
// Without `$width` it renders as a plain wrapper and the column sizes to its content as before.
export const CellContent = styled.div<{ $width?: number }>`
  box-sizing: border-box;

  ${({ $width }) =>
    $width &&
    css`
      width: ${$width}px;
      max-width: ${$width + CELL_PADDING_X * 2}px;
      overflow: hidden;
      text-overflow: ellipsis;
    `};
`

export const ActionsCell = styled.td`
  padding: 8px 12px;
  white-space: nowrap;
  position: sticky;
  right: 0;
  z-index: 1;
  background: ${colors.white100};

  ${RowTr}:hover & {
    background: ${colors.gray190};
  }
`

export const ActionsHeadCell = styled(HeadCell)`
  position: sticky;
  right: 0;
  z-index: 3;
  background: ${colors.white100};

  svg {
    height: 20px;
    margin-left: auto;
    position: relative;
    right: -52px;
    cursor: pointer;

    path {
      fill: ${colors.blue500};
    }

    &:hover {
      opacity: 0.8;
    }
  }
`

export const ActionIcon = styled(VerticalSplitIcon)<{ $color?: string }>`
  && {
    ${size('22px')};
    color: ${({ $color }) => $color || colors.gray300};
    flex-shrink: 0;
  }
`

export const MeasureIcon = styled(MeasuresIconOrigin)<{ $color?: string }>`
  && {
    ${size('22px')};
    flex-shrink: 0;

    path {
      fill: ${({ $color }) => $color || colors.gray300};
    }
  }
`

export const ObjectiveIcon = styled(ObjectivesIconOrigin)<{ $color?: string }>`
  && {
    ${size('22px')};
    color: ${({ $color }) => $color || colors.gray300};
    flex-shrink: 0;
  }
`

export const DescriptionContent = styled.div<{ $depth: number }>`
  height: 42px;
  display: flex;
  align-items: center;
  gap: 6px;
  user-select: none;
  ${({ $depth }) => css`
    padding-left: ${$depth * DEPTH_STEP}px;
  `}
`

export const DescriptionInnerContent = styled.div`
  display: flex;
  align-items: center;
  height: 42px;
  width: 100%;
  gap: 6px;
  overflow: hidden;
`

export const DragHandle = styled(DragIndicatorIcon)`
  && {
    ${size('16px')};
    flex-shrink: 0;
    cursor: grab;
    color: ${colors.gray300};
    opacity: 0;
  }

  ${RowTr}:hover & {
    opacity: 1;
  }
`

export const CollapseIcon = styled(ArrowForwardIcon)<{ $expanded: boolean }>`
  && {
    cursor: pointer;
    font-size: 18px;
    color: ${colors.gray300};
    flex-shrink: 0;
    ${({ $expanded }) =>
      $expanded &&
      css`
        transform: rotate(90deg);
      `};
  }
`

export const Title = styled.span`
  font-size: 16px;
  line-height: 1.25;
  color: ${colors.black};
  ${ellipsis('100%')};
`

// Full, untruncated title inside the hover popover — capped so a long one wraps instead of
// stretching the popover across the screen.
export const TitlePopoverContent = styled.div`
  max-width: 40vw;
`

// Invisible catch area below the table; only the standard depth-0 DropLine shows on hover.
export const RootDropZone = styled.div`
  position: relative;
  width: 100%;
  height: 16px;
`

export const FormRow = styled.tr`
  border-bottom: 1px solid ${colors.gray190};
`

export const FormCell = styled.td`
  padding: 4px 0;
`

// Pins the inline form to the left of the viewport and caps its width to the visible area,
// so it doesn't stretch across the full (horizontally scrollable) table.
export const FormStickyWrapper = styled.div`
  position: sticky;
  left: 0;
  box-sizing: border-box;
  padding: 0 12px;
  max-width: 1200px;
`

// The extra 16px keeps the form's input off the cell edge, in line with the row content above it.
export const FormIndent = styled.div<{ $depth: number }>`
  padding-left: ${({ $depth }) => $depth * DEPTH_STEP + FORM_INDENT_OFFSET}px;

  form {
    width: 100%;
  }
`

export const PlaceholderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`

export const LinkIconWithCounter = styled('div')<{ $background?: string; $isRootObjective?: boolean }>`
  align-items: center;
  color: ${colors.blue500};
  display: flex;
  font-size: 13px;
  height: 42px;
  margin-left: auto;
  position: relative;

  > div {
    position: absolute;
    top: 0;
  }

  svg {
    ${size('20px')};

    path {
      fill: ${colors.blue500};
    }
  }
`
